import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FaqService {

  constructor() { }
  /**
   * Retrieves a list of FAQs.
   * id: number - The unique identifier for the FAQ.
   * code: string - A unique code for the FAQ. Example: gen-01-001, Subject-section-question number.
   * question: string - The question being asked.
   * answer: string - The answer to the question.
   * @returns An array of FAQ categories, each containing a list of FAQs.
   *          Each FAQ includes an id, code, question, answer, and a flag indicating if the answer has been viewed.
   */
  getGeneralFAQ() {
    return [
      {
        category: 'Application',
        faqs: [
          {
            id: 1,
            code: 'gen-00-001',
            question: 'What does the red dot mean next to the About menu item?',
            answer: `The red badge on the About menu item is an indicator that 
            there are recent changes.  Navigate to the About menu then Version 
            Details, and you will see the latest 'New' updates.  Once all 
            updates have been viewed the red badge will disappear until the next 
            update or change.`,
            answerViewed: false,
          },
          {
            id: 2,
            code: 'gen-00-002',
            question: `Can I view my logbook in a browser?`,
            answer: `Yes.  In your browser, navigate to logbook.alliedpilots.org`,
            answerViewed: false,
          },
          {
            id: 3,
            code: 'gen-00-003',
            question: `Why do the same version updates show again on different devices?`,
            answer: `Currently, the version updates are stored in the local storage 
            of the device you are using.  Viewed updates are not synced across devices.
            There are plans to add this feature in a future release.`,
            answerViewed: false,
          },
          {
            id: 4,
            code: 'gen-00-004',
            question: `Why do I not see any of my flight time from previous airlines that merged
            with American Airlines?`,
            answer: `APA only has data for flights conducted from American Airlines, unfortunatley,
            flight data from merged airlines was not shared with APA and we have no access to this
            information.  However, if you have flight time from a previous airline there are future
            plans to add a featrue that will allow you to add this information to your logbook.`,
            answerViewed: false,
          }
        ]
      }
    ]
  }

  getFlightsFAQ() {
    return [
      {
        category: 'General',
        faqs: [
          {
            id: 1,
            code: 'flt-00-001',
            question: `Why are flights missing from my logbook?`,
            answer: `There may be several reasons why flights are missing from your logbook.
            Initially, during development of the Logbook Beta, the data was in the process of 
            being validated and may not have been included in the data load at different 
            intervals of the development stage.  Once the data is validated and corrections made 
            to the database, the logbook will be updated on a daily basis.  Other reasons for 
            missing flights could be due to the flight not being closed by AA, or the flight record
            is has corrupted data.  If you believe a flight is missing, please contact the APA IT by
            submitting feedback via the Feedback form located within the Logbook app.`,
            answerViewed: false,
          },
          {
            /** NOTE: If id or code changes for this quesiton, the moreInfo function in 
           * faq-more-info.component.ts will need to be updated.
           * The code is used to determine which data to load in the dialog content.
           */
            id: 2,
            code: 'flt-00-002',
            question: `Why doesn't the Flight Totals month match the Flights Table View list?`,
            answer: `The flight totals at the top of the Flights Table and Calendar View
            are based on what was actually flown per calendar month while the Flights Table 
            View will list all sequences that started in the contractual month of the  
            selected month.  This will make it possible for the Flight Totals for the month selected to 
            display flight time but not display any flights in the flights table list.  For example, if 
            you started a sequence December 1st, this sequence would be listed on the Table View list for 
            the month of November, but the actual flight time will be displayed for the month of December.  
            For a list of the contractual month in a year, select the link below.`,
            answerViewed: false,
            hasLink: true,
            linkTitle: `Contractual Months`,
          },
          /** NOTE: If id or code changes for this quesiton, the moreInfo function in 
           * faq-more-info.component.ts will need to be updated.
           * The code is used to determine which data to load in the dialog content.
           */
          {
            id: 3,
            code: 'flt-00-003',
            question: `What's the difference between calendar month and contractual month?`,
            answer: `The "Contractual Month" as used throughout the Collective Bargaining Agreement shall 
            mean the period of time, for pilot scheduling and pay purposes, during which allocated flying 
            and the associated PBS Awards shall be effective, in accordance with the table (link below).  
            The contractual month is used for the sequence/flight list and the Calendar so the user can 
            better identify what they flew in the contractual month.  A calendar month refers to the 
            duration of time from the first day of one month to the last day of the same month. It is based
             on the structure of the Gregorian calendar, with each calendar month having a specific number 
             of days.  Flight Totals, Analyze, and Charts are based on calendar month.   Because flights 
             are grouped by sequence and not individual flight trips, each sequence will be listed in the 
             contractual month in which they began.`,
            answerViewed: false,
            hasLink: true,
            linkTitle: `Contractual Months`
          }
        ]
      },
      {
        category: 'Table View',
        faqs: [
          {
            id: 1,
            code: 'flt-01-001',
            question: 'How do I filter out Cancelled and Deadhead flights?',
            answer: `From the Flights Table View select the filter Icon.  
            From here, remove the desired flight type from the list by toggling off 
            the switch. At least one filter item must be left on.  After selection, 
            press the Save button to commit your changes.`,
            answerViewed: false,
          },
          {
            id: 2,
            code: 'flt-01-002',
            question: 'How do I display the flights list in UTC time or Local time?',
            answer: `From the Flights Table View, select the filter icon.  
            From here select either UTC or Local.  Press the Save button to commit 
            your changes.  Your flights will then be listed by the time you selected.  
            This selection is a global change and will be reflected in the Flight 
            details view and the Sequence details view.`,
            answerViewed: false,
          },
          {
            id: 3,
            code: 'flt-01-003',
            question: 'Can I see the details of the entire sequence?',
            answer: `Yes, on the flight list simply tap on the sequence number. This 
            will take you to the sequence details view where you can see all flights 
            associated with that sequence.`,
            answerViewed: false,
          },
          {
            id: 4,
            code: 'flt-01-004',
            question: 'Why is the aircraft nose number empty?',
            answer: `When a flight is completed it can take up to 6 or 7 days for the aircraft 
            information to be retrieved from AA servers.  Although, there are times APA is not 
            able to retrieve this information.`,
            answerViewed: false,
          },
          {
            id: 5,
            code: 'flt-01-005',
            question: 'What do the small green, yellow and red icons mean on the Table View?',
            answer: `These refer to the current flight list filter status.  It is provided 
            as a quick reference to advise which filters are currently applied.`,
            answerViewed: false,
          },
          {
            id: 6,
            code: 'flt-01-006',
            question: 'How do I refresh the flight list?',
            answer: `From the Flights Table View, select the double round arrows button.  
            This will refresh your current view of flights only, e.g., selected month is December 
            2024. Selecting refresh icon will only refresh flights for December 2024.`,
            answerViewed: false,
          },
          {
            id: 7,
            code: 'flt-01-007',
            question: 'How do I change the list sort order?',
            answer: `From the Flights Table view, select the filter icon to open the 
            Sort/Filter Flights dialog popup. The label name 'Sort' identifies which sort order is
            currently selected.  Tapping the blue Ascending or Descending will change the 
            sort order.`,
            answerViewed: false,
          },

          /** NOTE: If id or code changes for this quesiton, the moreInfo function in 
           * faq-more-info.component.ts will need to be updated.
           * The code is used to determine which data to load in the dialog content.
           */
          {
            id: 8,
            code: 'flt-01-008',
            question: `Why does a preceding or next month flight(s) sometimes appear in the flight list 
            for the selected month?`,
            answer: `Monthly flights are displayed based on the contractual month.  
            This means that flights that start in the previous month and end in the selected 
            month could be displayed in the selected month as part of that contractual month.  
            The same applies to flights that start in the selected month and end in the next month. 
            All sequences are listed in the contractual month in which they began.
            For a list of the effective periods in a year, select the link below.`,
            answerViewed: false,
            hasLink: true,
            linkTitle: `Contractual Months`,
          },
          {
            id: 9,
            code: 'flt-01-09',
            question: `Why are my OE Upgrade training flights logged as Captain instead of First Officer?`,
            answer: `This as a known issue, and we are working to resolve the issue.  If conducting 
            operational experience as an upgrade captain the fight time will be logged as Captain instead 
            of First Officer until a fix is implemented.  While a pilot is conducting his training with a 
            Check Airman his actual flight time should be logged as First Officer until such time as he/she 
            has completed their upgrade operational experience.  Once the operational experience training is 
            complete the new upgrade pilot can log his/her fight time as Captain. `,
            answerViewed: false,
          },
          {
            id: 10,
            code: 'flt-01-010',
            question: `Why isn't the RFD part of actual flight time?`,
            answer: `RFD (Ready For Departure) time is not considered part of actual flight time.  It is 
              considered part of the duty time and pay caluculations.  The RFD time is the time the aircraft 
              is ready for departure.\n\n
              Reference: “It is important to note that these RFD times are for pay and credit purposes 
              only and not for FAR flight time legalities. Flight time (block-to-block time) is defined 
              in 2023 CBA Section 15.C.11 as the time from the moment the airplane first moves for the 
              purpose of flight (OUT time) until it comes to rest at the next point of landing (IN time).`,
            answerViewed: false,
          },
          {
            id: 11,
            code: 'flt-01-011',
            question: `Why is the wrong aircraft type displayed?`,
            answer: `Upon retrieval of a flight from American Airlines (AA), each flight is assigned an 
            equipment code specifically designated for that particular flight. This equipment code is subsequently 
            cross-referenced with a table that provides the aircraft type name and variant name. Regrettably, 
            over the years and the extensive historical aircraft operated by AA, these equipment codes have been 
            reused for newer aircraft, leading to complications in cross-referencing. Despite the efforts of APA 
            IT in implementing several patches to address this issue, there may still be instances where the 
            incorrect aircraft type is displayed, along with either missing or incorrect variant information for 
            that type.`,
            answerViewed: false,
          }
        ]
      },
      {
        category: 'Calendar View',
        faqs: [
          {
            id: 1,
            code: 'flt-02-001',
            question: `Why aren't the calendar month and year selectable?`,
            answer: `In its current form, this feature is not supported.  To navigate the month and year, utilize 
            the preceding (<) and subsequent (>) buttons for the month, and the preceding (<<) and subsequent (>>) 
            buttons for the year.  The calendar will display the completed flight sequences for the contractual month.`,
            answerViewed: false,
          },
          {
            id: 2,
            code: 'flt-02-001',
            question: 'How do I see the sequence details that are on the calendar?',
            answer: `Simply tap on that sequence and it will direct you to the sequence detail view.`,
            answerViewed: false,
          }
        ]
      },
      {
        category: 'Flight Details',
        faqs: [
          {
            id: 1,
            code: 'flt-03-001',
            question: 'Why is the aircraft type blank?',
            answer: `When the data is retrieved from AA there are times the aircraft type 
            and/or nose number have not been associated with a particular flight.  Other 
            times it may take up to 7 days for the aircraft type to appear while awaiting 
            the flight to be closed from AA.  APA makes every attempt to capture this data 
            but there are occasions it is never populated due to unknown reasons.`,
            answerViewed: false,
          },
          {
            id: 2,
            code: 'flt-03-002',
            question: 'Why is the aircraft nose number blank?',
            answer: `When the data is retrieved from AA there are times the aircraft type 
            and/or nose number have not been associated with a particular flight.  Other 
            times it may take up to 7 days for the aircraft type to appear while awaiting 
            the flight to be closed from AA.  APA makes every attempt to capture this data 
            but there are occasions it is never populated due to unknown reasons.`,
            answerViewed: false,
          },
          {
            id: 3,
            code: 'flt-03-003',
            question: 'How do I contact the pilot for a particular flight?',
            answer: `On the Flight details page there is an icon next to each pilot name.  
            Pressing this will open a popup with that pilot's contact details.  These contact 
            details follow the APA guidelines on public or private contact information.  If you 
            find your own contact details do not match your desired privacy preference you can  
            change them on the APA website. Select your user icon from the top right hand corner
            of the Home page, then select 'Update Your Profile.'  Privacy Preferences, set them
            either as  General Release, Official Use Only or Emergency Use Only.`,
            answerViewed: false,
          },
          {
            id: 4,
            code: 'flt-03-004',
            question: 'What does the orange tag of Surface Duty/Miscellaneous mean?',
            answer: `This is a designation by AA for a flight record that was not an actual flight.  
            Although it has been identified as a deadhead flight, it is typically a surface or 
            positioning movement via ground transportation between stations.  In these cases AA will
            typically designate the flight number as 0.`,
            answerViewed: false,
          },
          // {
          //   id: 5,
          //   code: 'flt-03-005',
          //   question: 'Where can I find the aircraft registration number?',
          //   answer: `The aircraft registration number is not provided by AA.  APA IT is working on a way 
          //   to provide this information.  Once obtained this will be added as part of the flight details.`,
          // },
          {
            id: 5,
            code: 'flt-03-006',
            question: `Why is the wrong aircraft type displayed?`,
            answer: `Please refer to the response provided for this question under the “Table View” category.`,
            answerViewed: false,
          }
        ]
      },
      {
        category: 'Sequence Details',
        faqs: [
          {
            id: 1,
            code: 'flt-04-001',
            question: 'How can I change the date sort order?',
            answer: `Currently, a seperate option for this is not available.  There are plans to allow 
            sorting flights by ascending or descending order seperate from the flight table list.  The 
            sorting selections made on the flight table list will also be reflected in the sequence details.`,
            answerViewed: false,
          }
        ]
      },
    ]
  }

  getAnalyzeFAQ() {
    return [
      {
        category: 'Flight Totals',
        faqs: [
          {
            id: 1,
            code: 'ana-01-001',
            question: `Why doesn't the total time match my DECS HI*9?`,
            answer: `As Logbook Beta is being tested the data retrieved is 
            being validated. As we progress in this validation process your 
            flight time will be equal to or close to your DECS HI*9 value.  
            Although, the total time being displayed is calculated from actual 
            flight times that were provided in the retrieved and historical data, 
            it is possible that some flights may have missing flight time 
            information which in turn will affect the calculated total 
            flight time.`,
            answerViewed: false,
          },
          {
            id: 2,
            code: 'ana-01-002',
            question: 'Are there any plans to add nighttime flying to the Flight Totals?',
            answer: `Yes, there are plans to add these flying totals.  However, these 
            are not provided by AA and must be calculated from algorithms 
            designed by APA developers.`,
            answerViewed: false,
          },
          {
            id: 3,
            code: 'ana-01-003',
            question: `What does Distance Flown indicate?`,
            answer: `This indicates the total distance flown with American Airlines (AA) that 
            you have flown, based on the current database records. Please note that if there 
            are any known or unknown gaps in the data, this number may be inaccurate.`,
            answerViewed: false,
          },
          {
            id: 4,
            code: 'ana-01-004',
            question: `What does Passenger Count indicate?`,
            answer: ` It indicates the total passenger count for all American Airlines flights. 
            Unfortunately, passenger count data collection began only in 2021. Previous years' 
            data are calculated averages and don't represent actual passenger counts.`,
            answerViewed: false,
          }
        ]
      },
      {
        category: 'Charts',
        faqs: [
          {
            id: 1,
            code: 'ana-02-001',
            question: `Why don't the flight times in the charts match those in Flight Totals?`,
            answer: `The flight time displayed on the Flight Totals view is formatted 
            as hh:mm while the charts are formatted as a decimal value.`,
            answerViewed: false,
          },
          {
            id: 2,
            code: 'ana-02-002',
            question: `Why are my OE Upgrade training flights logged as Captain instead of First Officer?`,
            answer: `This as a known issue, and we are working to resolve the issue.  If conducting 
            operational experience as an upgrade captain the fight time will be logged as Captain instead 
            of First Officer until a fix is implemented.  While a pilot is conducting his training with a 
            Check Airman his actual flight time should be logged as First Officer until such time as he/she 
            has completed their upgrade operational experience.  Once the operational experience training is 
            complete the new upgrade pilot can log his/her fight time as Captain. `,
            answerViewed: false,
          },
          {
            id: 3,
            code: 'ana-02-003',
            question: `Why is the wrong aircraft type displayed? `,
            answer: `Upon retrieval of a flight from American Airlines (AA), each flight is assigned an 
            equipment code specifically designated for that particular flight. This equipment code is 
            subsequently cross-referenced with a table that provides the aircraft type name and variant 
            name. Regrettably, over the years and the extensive historical aircraft operated by AA, these 
            equipment codes have been reused for newer aircraft, leading to complications in cross-referencing. 
            Despite the efforts of APA IT in implementing several patches to address this issue, there may 
            still be instances where the incorrect aircraft type is displayed, along with either missing or 
            incorrect variant information for that type.`,
            answerViewed: false,
          },
          {
            id: 4,
            code: 'ana-02-004',
            question: `What does aircraft type NA mean?`,
            answer: `An aircraft type of NA indicates that the aircraft type was not available when creating
            the chart.  This could be due to the aircraft type not being available in the database or the
            aircraft type was not provided by AA.  APA IT is working to resolve this issue.`,
            answerViewed: false,
          }
        ]
      }
    ]
  }

  getSearchFAQ() {
    return [
      {
        category: 'Search Form',
        faqs: [
          {
            id: 1,
            code: 'sea-01-001',
            question: `What's the difference between Specific Date and Date Range?`,
            answer: `When completing the search form you are given two choices for date.  
            Entering specific date will search for details based on that specific date or 
            that date and other criteria entered.  Date Range searches for details over a 
            period of time.  For a date range, you MUST enter a beginning and end date.
            If one is blank, you will receive an error advising the one date is missing.  
            Only one date type can be completed, either Specific Date or Date Range.  If 
            you enter a date in one field, the other must be empty.`,
            answerViewed: false,
          },
          {
            id: 2,
            code: 'sea-01-002',
            question: `I don't know the equipment number. Can I search for all of one type of Aircraft?`,
            answer: `Unfortunately, at this time the algorithm is not designed for this.  This is a 
            planned feature to be added at a later date.`,
            answerViewed: false,
          },
          {
            id: 3,
            code: 'sea-01-003',
            question: `Why can't I search by aircraft registration number?`,
            answer: `This feature has not yet been added to the Search functionality.  There are plans to 
            add this in a future release.`,
            answerViewed: false,
          },
          {
            id: 4,
            code: 'sea-01-004',
            question: 'Why do I have to enter a last name if I have a first name only?',
            answer: `Due to the large pilot group and the possibility of common first names, 
            the algorithm will only return a pilot name if a last name 
            and/or first and last name are provided.  This is by design due to such a large 
            pilot group.`,
            answerViewed: false,
          },
          {
            id: 5,
            code: 'sea-01-005',
            question: `Is it possible to search a partial or wildcard (*) name?`,
            answer: `No, the search algorithm is not designed to search for a partial name or 
            use wildcard asterisk as part of the name search.  When searching for a name it 
            must match the exact spelling of the name being searched.  The algorithm will 
            search for the first matched last name.  There are future plans to enhance the 
            search performance at a later date.`,
            answerViewed: false,
          }

        ]
      },
      {
        category: 'Search Results',
        faqs: [
          {
            id: 1,
            code: 'sea-02-001',
            question: 'How do know if there are multiple page results?',
            answer: `If your search query results in more than 20 flight records, an additional 
            page will be added, and will continue to add additional pages until reaching the 
            end of your results.  The paging navigator will only appear when your results exceed 
            the 20-record limit.  Otherwise, the paging navigator will not appear.`,
            answerViewed: false,
          },
          {
            id: 2,
            code: 'sea-02-002',
            question: 'How do I see the criteria I entered on the search form?',
            answer: `If you would like to see what you entered without 
            having to navigate back to the search form, simply select the Search Criteria title to 
            open a view of the form.`,
            answerViewed: false,
          },
          {
            id: 3,
            code: 'sea-02-003',
            question: `How can I sort the search results?`,
            answer: `The search function currently lacks sorting options for returned results. When 
            a request is made based on user input, the server returns the results based on the most 
            recent activity.`,
            answerViewed: false,
          }
        ]
      }
    ]
  }

  getExportFAQ() {
    return [
      {
        category: 'Exporting',
        faqs: [
          {
            id: 1,
            code: 'exp-01-001',
            question: `Why can't I export my logbook?`,
            answer: `There are several reasons why you may not be able to export your logbook.  The most
            common reason is that you have not selected any of the checkboxes.  You must select at least
            one checkbox to export your logbook.  It is also possible you have reached the 2 limit maximum
            download restriction for your log in. If you are still having trouble downloading your logbook, 
            please contact the APA ITSC department by submitting feedback via the Feedback form
            located within the Logbook app.`,
            answerViewed: false,
          },
          {
            id: 1,
            code: 'exp-01-002',
            question: `I'm on a different device. Why can't I see my previous downloads?`,
            answer: `The download history is stored in the local storage of the device you are using.
            Download history is not synced across devices.  There are plans to add this feature in a 
            future release.  Keep in mind that you can only download your logbook twice per hour.  If you have
            reached the 2 limit maximum download restriction for your log in, you will not be able to download
            your logbook until 1 hour has passed from the first download.  If you are still having 
            trouble downloading your logbook, please contact the APA ITSC department by submitting 
            feedback via the Feedback form.`,
            answerViewed: false,
          },
          {
            id: 1,
            code: 'exp-01-003',
            question: `Can I get my logbook in PDF format?`,
            answer: `Currently, the only format available is CSV.  There are plans to add PDF format in a
            future release.  The CSV format is compatible with most spreadsheet applications such as
            Microsoft Excel, Google Sheets, and Apple Numbers.  Once the CSV file is downloaded, you can
            open it in your preferred spreadsheet application and then save or export it as a PDF.`,
            answerViewed: false,
          },
          {
            id: 1,
            code: 'exp-01-001',
            question: `Can I export the CVS file directly to a 3rd party application?`,
            answer: `Yes you can.  Once the CSV file is downloaded, you can open it in your preferred 
            application.  You will need to follow the instructions for that application to
            import the CSV file.  The CSV format is compatible with most spreadsheet applications.`,
            answerViewed: false,
          }
        ]
      },
      {
        category: 'Career Map Projection',
        faqs: [
          {
            id: 1,
            code: 'exp-02-001',
            question: `Can I export my career map to an image file or other format?`,
            answer: `Currently there is no option to export the career map. 
            We are working to provide this option in a future release.`,
            answerViewed: false
          },
          {
            id: 2,
            code: 'exp-02-002',
            question: `Can the color of the route lines be changed when displaying by aircraft type?`,
            answer: `Currently this opton is not supported.  The color of the route lines is
            determined by the aircraft type.  The color of the route lines is not changeable.`,
            answerViewed: false
          },
        ]
      }
    ];
  }

  getAdminFAQ() {
    return [
      {
        category: 'General',
        faqs: [
          {
            id: 1,
            code: 'adm-01-001',
            question: 'How do I emulate an employee number?',
            answer: `Navigate to the Admin menu and insert the employee number in the form field for 
            Employee Emulator. Press the Emulate button; this will automatically direct you to the 
            Flight Table view with the emulated employee.  To reset back to your own logbook, press 
            the Reset button from the Admin page or from the red icon in the top right corner of the 
            top title bar.`,
            answerViewed: false,
          }
        ]
      }
    ]
  }
}
